<template>
  <div class="p-4">
    <div class="flex justify-between items-center mb-4">
      <!-- Filter Dropdown -->
      <b-dropdown text="Filter" variant="primary" class="mr-2">
        <b-dropdown-item @click="updateFilter(null)">All</b-dropdown-item>
        <b-dropdown-item @click="updateFilter(true)">Active</b-dropdown-item>
        <b-dropdown-item @click="updateFilter(false)">Inactive</b-dropdown-item>
      </b-dropdown>

      <!-- Generate Key Button -->
      <b-button variant="success" @click="showGenerateModal = true">
        Generate API Key
      </b-button>
    </div>

    <!-- API Keys Table -->
    <b-table
      :items="apiKeys"
      :fields="fields"
      striped
      hover
      responsive
      class="shadow-md rounded-lg"
      :busy="isLoading"
    >
      <template #cell(displayKey)="data">
        <div 
          class="api-key-cell"
          :title="data.item.metadata && data.item.metadata.displayKey"
        >
          {{ data.item.metadata && data.item.metadata.displayKey || '-' }}
        </div>
      </template>
      
      <template #cell(additionalNote)="data">
        <div 
          class="api-key-cell"
          :title="data.item.metadata && data.item.metadata.additionalNote"
        >
          {{ data.item.metadata && data.item.metadata.additionalNote || '-' }}
        </div>
      </template>
      
      <template #cell(active)="data">
        <span
          :class="data.item.active ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'"
        >
          {{ data.item.active ? 'Active' : 'Inactive' }}
        </span>
      </template>
      
      <template #cell(actions)="data">
        <div class="action-buttons">
          <!-- Toggle Active/Inactive Button -->
          <b-button
            :variant="data.item.active ? 'warning' : 'success'"
            size="sm"
            :disabled="isLoading"
            @click="toggleApiKeyStatus(data.item)"
          >
            {{ data.item.active ? 'Deactivate' : 'Activate' }}
          </b-button>
          
          <!-- Copy Button -->
          <b-button
            variant="info"
            size="sm"
            title="Copy API Key"
            @click="copyApiKey(data.item.metadata && data.item.metadata.displayKey)"
          >
            <i class="fa fa-copy"></i>
          </b-button>
          
          <!-- Update Note Button -->
          <b-button
            variant="secondary"
            size="sm"
            title="Update Additional Note"
            :disabled="isLoading"
            @click="openUpdateModal(data.item)"
          >
            <i class="fa fa-edit"></i>
          </b-button>
          
          <!-- Delete Button -->
          <b-button
            variant="danger"
            size="sm"
            title="Delete API Key"
            :disabled="isLoading"
            @click="handleDeleteApiKey(data.item)"
          >
            <i class="fa fa-trash"></i>
          </b-button>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="flex justify-center mt-4">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPages"
        align="center"
        @change="changePage"
      ></b-pagination>
    </div>

    <!-- Generate Key Modal -->
    <b-modal v-model="showGenerateModal" title="Generate API Key" hide-footer>
      <b-form @submit.prevent="handleGenerateKey">
        <b-form-group label="Account ID" label-for="account-id">
          <b-form-input
            id="account-id"
            v-model="newAccountId"
            placeholder="Enter Account ID"
            required
          ></b-form-input>
        </b-form-group>
        <b-form-group label="Additional Note" label-for="additional-note">
          <b-form-textarea
            id="additional-note"
            v-model="newAdditionalNote"
            placeholder="Enter additional note (optional)"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
        <div class="flex justify-end">
          <b-button variant="secondary" @click="showGenerateModal = false">
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" class="ml-2">
            Generate
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Update Note Modal -->
    <b-modal v-model="showUpdateModal" title="Update Additional Note" hide-footer>
      <b-form @submit.prevent="handleUpdateNote">
        <b-form-group label="Additional Note" label-for="update-additional-note">
          <b-form-textarea
            id="update-additional-note"
            v-model="updateAdditionalNote"
            placeholder="Enter additional note"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
        <div class="flex justify-end">
          <b-button variant="secondary" @click="showUpdateModal = false">
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" class="ml-2">
            Update
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Generated API Key Modal -->
    <b-modal v-model="showGeneratedKeyModal" title="API Key Generated Successfully" hide-footer @hidden="closeGeneratedKeyModal">
      <div class="text-center">
        <div class="alert alert-warning mb-4">
          <h5><i class="fa fa-exclamation-triangle mr-2"></i>Important!</h5>
          <p class="mb-0">
            <strong>Please save this API key immediately!</strong><br>
            This is the only time you will be able to see the complete key. 
            Make sure to copy and store it securely before closing this modal.
          </p>
        </div>
        
        <b-form-group label="Your API Key" label-for="generated-key">
          <b-form-input
            id="generated-key"
            :value="generatedApiKey"
            readonly
            class="text-center font-monospace"
            style="font-size: 14px;"
          ></b-form-input>
        </b-form-group>
        
        <div class="mt-3">
          <b-button variant="info" class="mr-2" @click="copyGeneratedApiKey">
            <i class="fa fa-copy mr-1"></i> Copy Key
          </b-button>
          <b-button variant="success" @click="closeGeneratedKeyModal">
            <i class="fa fa-check mr-1"></i> I've Saved It
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  data() {
    return {
      showGenerateModal: false,
      showUpdateModal: false,
      showGeneratedKeyModal: false,
      newAccountId: '',
      newAdditionalNote: '',
      currentEditApiKey: null,
      updateAdditionalNote: '',
      generatedApiKey: '',
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'displayKey', label: 'API Key' },
        { key: 'actorId', label: 'Account ID' },
        { key: 'additionalNote', label: 'Additional Note' },
        { key: 'active', label: 'Status' },
        { key: 'createdAt', label: 'Created At' },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
    }
  },
  computed: {
    ...mapState('scimApiKey', ['apiKeys', 'pagination', 'isLoading']),
    currentPage: {
      get() {
        return Number(this.$route.query.page || 1)
      },
      set(val) {
        this.changePage(val)
      },
    },
    perPages() {
      return this.pagination && this.pagination.limit
    },
    totalRows() {
      return this.pagination && this.pagination.total
    },
  },
  created() {
    this.fetchKeysFromQuery()
  },
  watch: {  
    '$route.query': 'fetchKeysFromQuery',
  },
  methods: {
    ...mapActions('scimApiKey', ['fetchApiKeys', 'generateApiKey', 'updateApiKey', 'updateApiKeyMetadata', 'deleteApiKey']),
    fetchKeysFromQuery() {
      const { page = 1, limit = 40, active } = this.$route.query
      this.fetchApiKeys({
        page: Number(page),
        limit: Number(limit),
        active: active === undefined ? undefined : active === 'true',
      })
    },
    updateFilter(active) {
      this.$router.push({
        query: {
          ...this.$route.query,
          active: active === null ? undefined : active,
          page: 1,
        },
      })
    },
    changePage(page) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page,
        },
      })
    },
    async handleGenerateKey() {
      try {
        const metadata = this.newAdditionalNote ? { additionalNote: this.newAdditionalNote } : {}
        const response = await this.generateApiKey({ 
          accountId: this.newAccountId,
          metadata: metadata
        })
        
        // Close generate modal
        this.showGenerateModal = false
        this.newAccountId = ''
        this.newAdditionalNote = ''
        
        // Show the generated key modal
        this.generatedApiKey = response.key || ''
        this.showGeneratedKeyModal = true
        
      } catch (err) {
        console.error(err)
        this.$toasted.show('Failed to generate API Key', {
          type: 'error',
          duration: 3000
        })
      }
    },
    async toggleApiKeyStatus(apiKey) {
      try {
        const newStatus = !apiKey.active
        await this.updateApiKey({ 
          apiKeyId: apiKey.id, 
          active: newStatus 
        })
        
        // Show success toast
        this.$toasted.show(`API Key ${newStatus ? 'activated' : 'deactivated'} successfully!`, {
          type: 'success',
          duration: 3000
        })
        
        // Reload the page
        this.fetchKeysFromQuery()
      } catch (err) {
        console.error(err)
        this.$toasted.show('Failed to update API Key status', {
          type: 'error',
          duration: 3000
        })
      }
    },
    async copyApiKey(apiKey) {
      if (!apiKey) {
        this.$toasted.show('No API Key to copy', {
          type: 'warning',
          duration: 2000
        })
        return
      }
      
      try {
        await navigator.clipboard.writeText(apiKey)
        this.$toasted.show('API Key copied to clipboard!', {
          type: 'success',
          duration: 2000
        })
      } catch (err) {
        console.error('Failed to copy API Key:', err)
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = apiKey
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          this.$toasted.show('API Key copied to clipboard!', {
            type: 'success',
            duration: 2000
          })
        } catch (fallbackErr) {
          this.$toasted.show('Failed to copy API Key', {
            type: 'error',
            duration: 3000
          })
        }
        document.body.removeChild(textArea)
      }
    },
    async copyGeneratedApiKey() {
      await this.copyApiKey(this.generatedApiKey)
    },
    closeGeneratedKeyModal() {
      this.showGeneratedKeyModal = false
      this.generatedApiKey = ''
      // Refresh the data to show the new API key in the table
      this.fetchKeysFromQuery()
    },
    openUpdateModal(apiKey) {
      this.currentEditApiKey = apiKey
      this.updateAdditionalNote = apiKey.metadata && apiKey.metadata.additionalNote || ''
      this.showUpdateModal = true
    },
    async handleUpdateNote() {
      try {
        const metadata = { additionalNote: this.updateAdditionalNote }
        await this.updateApiKeyMetadata({ 
          apiKeyId: this.currentEditApiKey.id,
          metadata: metadata
        })
        
        this.showUpdateModal = false
        this.currentEditApiKey = null
        this.updateAdditionalNote = ''
        
        // Show success toast
        this.$toasted.show('Additional note updated successfully!', {
          type: 'success',
          duration: 3000
        })
        
        // Reload the page
        this.fetchKeysFromQuery()
      } catch (err) {
        console.error(err)
        this.$toasted.show('Failed to update additional note', {
          type: 'error',
          duration: 3000
        })
      }
    },
    async handleDeleteApiKey(apiKey) {
      // Show confirmation dialog
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete this API Key? This action cannot be undone.`,
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )
      
      if (!confirmed) {
        return
      }
      
      try {
        await this.deleteApiKey({ apiKeyId: apiKey.id })
        
        // Show success toast
        this.$toasted.show('API Key deleted successfully!', {
          type: 'success',
          duration: 3000
        })
        
        // Reload the page
        this.fetchKeysFromQuery()
      } catch (err) {
        console.error(err)
        this.$toasted.show('Failed to delete API Key', {
          type: 'error',
          duration: 3000
        })
      }
    },
  },
}
</script>

<style scoped>
.api-key-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}


</style>
